import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Constants } from '../../classes/constants';
import { Subject } from 'rxjs';
import { sortArrayOfObjectsBy, normalizeSlug } from '../../library/utils';
import { Job, JobCategory } from '../../classes/job';
import { Client } from '../../partners/models/client';
import { ClientService } from '../../partners/services/client.service';
import { MatDialog } from '@angular/material/dialog';
import { JobsSubscriptionComponent } from './jobs-subscription/jobs-subscription.component';
import { AlertsService } from '../../services/alerts.service';
import { Subscription } from '../../partners/models/subscription';
import { SafeStyle, DomSanitizer } from '@angular/platform-browser';
import { JobsSubscriptionFirstStepComponent } from './jobs-subscription-first-step/jobs-subscription-first-step.component';
import { injectScript } from '../../utils';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-client-hub',
  templateUrl: './client-hub.component.html',
  styleUrls: [ './client-hub.component.scss' ]
})
export class ClientHubComponent implements OnInit, OnDestroy {

  loading = false;
  loadingCategories = false;

  client_uuid: string;
  jobs: any[] = [];
  filteredJobs: any[] = [];
  base_url = Constants.BASE_URL.slice(0, -1);
  LEGAL_DATA_URL = Constants.GROUP_URL_LEGAL_DATA;
  client: Client;
  clientIsAllowed = false;
  isPrivate = false;
  hasSubscriptions: boolean = false;

  displayClosedMessage = false;

  categoriesFilters = {
    PRO: [],
    ORG: [],
    FUN: [],
    LOC: [],
  };

  filteredCategoriesFilters = {
    PRO: [],
    ORG: [],
    FUN: [],
    LOC: [],
  };

  selectedFilters = [];

  displayFilterPopup = {
    PRO: false,
    ORG: false,
    FUN: false,
    LOC: false,
  };

  searchTerm = {
    PRO: new Subject<string>(),
    ORG: new Subject<string>(),
    FUN: new Subject<string>(),
    LOC: new Subject<string>(),
  };

  parsedCategoriesGroups = {
    PRO: '__profession',
    ORG: '__organization',
    FUN: '__function',
    LOC: '__localization',
  };

  /**
   * Query params aceptados por cada grupo de filtros. El primer nombre es el
   * canonico, el que se escribe en la URL al filtrar desde la UI. Los siguientes
   * son alias que se siguen leyendo para no invalidar enlaces ya publicados.
   *
   * Para filtrar por varios valores de un grupo se repite el parametro:
   *   ?location=madrid&location=a-coruna
   */
  urlFilterParams = {
    PRO: [ 'profession', 'profession_filter' ],
    ORG: [ 'organization', 'organization_filter' ],
    FUN: [ 'function', 'function_filter' ],
    LOC: [ 'location', 'location_filter' ],
  };

  /**
   * Tokens de filtro leidos de la URL, a la espera de que lleguen las categorias
   * del cliente para poder resolverlos. Ver applyFiltersFromUrl.
   */
  pendingUrlFilters = {
    PRO: [],
    ORG: [],
    FUN: [],
    LOC: [],
  };

  componentSubscriptions: any = {};

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private clientService: ClientService,
    private dialog: MatDialog,
    private alertService: AlertsService,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    this.client_uuid = this.activatedRoute.snapshot.paramMap.get('client');
    this.isPrivate = this.activatedRoute.snapshot.url[ 0 ].path === 'private-jobs-hub';
    this.readFiltersFromUrl();
    this.getClientData(this.client_uuid);
    this.isPrivate ? this.getPrivateJobs(this.client_uuid) : this.getJobs(this.client_uuid);
    this.doSearch();
  }

  ngOnDestroy() {
    Object.keys(this.componentSubscriptions).forEach(
      subscriptionKey => this.componentSubscriptions[ subscriptionKey ].unsubscribe()
    );
  }

  getClientData(client_uuid: string) {
    this.componentSubscriptions.clientServiceGetPublicClient$ = this.clientService.getPublicClient(client_uuid)
      .subscribe(
        response => {
          this.client = response;
          if (response.logo && response.logo.indexOf('http') === -1) {
            this.client.logo = `${Constants.FILE_URL}${response.logo}`;
          }
          if (this.client.extra_config.appearance) {
            this.setClientFavicon();
          }
          this.addCookieBot();
          this.setupSubscriptions();
          if (response.extra_config.landbot) {
            injectScript(response.extra_config.landbot, this.elementRef);
          }
        },
        error => this.displayClosedMessage = true
      );
  }


  getSanitizedClientFont(): SafeStyle {
    const font = this.client.extra_config.appearance.font.primary ? this.client.extra_config.appearance.font.primary : 'Roboto, sans-serif';

    return this.sanitizer.bypassSecurityTrustStyle(`font-family:${font} !important;`);
  }

  setClientFavicon() {
    const link: any = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = this.client.extra_config.appearance.favicon ? this.client.extra_config.appearance.favicon : 'favicon.ico';
    document.getElementsByTagName('head')[ 0 ].appendChild(link);
  }

  getJobs(client_uuid: string) {
    this.loading = true;
    this.componentSubscriptions.clientServiceGetPublicProfessions$ = this.clientService.getPublicProfessions(client_uuid)
      .subscribe(
        response => {
          this.jobs = this._serializeJobs(response);
          this.filteredJobs = this.jobs;
          this._serializeFilterCategories();
          this.clientIsAllowed = true;
          this.loading = false;
        },
        error => this.displayClosedMessage = true
      );
  }

  getPrivateJobs(client_uuid: string) {
    this.loading = true;
    this.componentSubscriptions.clientServiceGetPrivateProfessions$ = this.clientService.getPrivateProfessions(client_uuid)
      .subscribe(
        response => {
          this.jobs = this._serializeJobs(response);
          this.filteredJobs = this.jobs;
          this._serializeFilterCategories();
          this.clientIsAllowed = true;
          this.loading = false;
        },
        error => console.log(error)
      );
  }

  _serializeJobs(professions: JobsByProfession[]) {
    const jobs_show = professions
      .filter(profession => profession.jobs.length > 0)
      .reduce((jobs, profession) => jobs = jobs.concat(this._addProfessionToJobs(profession)), [])
      .map(job => {
        job.categories = this.sortJobCategories(job.categories);
        return job;
      });
    return sortArrayOfObjectsBy(jobs_show, 'name', 1);
  }

  _addProfessionToJobs(profession: JobsByProfession): Job[] {
    return profession.jobs.map(job => {
      job.profession_name = profession.name;
      return job;
    });
  }

  sortJobCategories(categories: JobCategory[]) {
    const getCategoriesOrder = (category: JobCategory) => {
      const categoriesOrder = Object.keys(this.categoriesFilters);
      return categoriesOrder.indexOf(category.category);
    };

    const sortedCategories = categories.sort((a, b) => getCategoriesOrder(a) - getCategoriesOrder(b));
    return sortedCategories;
  }

  _serializeFilterCategories() {

    this.loadingCategories = true;

    this.componentSubscriptions.clientServiceGetJobCaegories$ = this.clientService.getJobCategoriesByUUID(this.client_uuid)
      .subscribe(
        categories => {

          const jobCategories = categories.map(category => {
            return {
              'id': category[ 'id' ],
              'category': category[ 'category' ],
              'value': category[ 'value' ],
              'display_value': category[ 'display_value' ]
            };
          });

          Object.keys(this.categoriesFilters)
            .forEach(categoryGroup => {
              const categoriesBycategoryGroup = jobCategories.filter(jobCategory => jobCategory.category === categoryGroup && jobCategory.value.toUpperCase() !== 'ALL');
              this.categoriesFilters[ categoryGroup ] = sortArrayOfObjectsBy(categoriesBycategoryGroup, 'value', 1);
              this.filteredCategoriesFilters[ categoryGroup ] = this.categoriesFilters[ categoryGroup ];
            });

          this.applyFiltersFromUrl();

          this.loadingCategories = false;
        },
        error => console.log(error)
      );
  }


  doSearch(): void {

    Object.keys(this.searchTerm)

      .forEach(categoryGroup => {

        this.componentSubscriptions.searchTerm$ = this.searchTerm[ categoryGroup ].pipe(
          debounceTime(500),
          distinctUntilChanged()
        ).subscribe(search => {
          return this.filterCategories(search, categoryGroup);
        });

      });
  }

  setupSubscriptions() {
    switch (this.client.extra_config.alert_subscriptions) {
      case 'BTH':
        this.hasSubscriptions = true;
        break;
      case 'PUB':
        this.hasSubscriptions = this.isPrivate ? false : true;
        break;
      case 'PRI':
        this.hasSubscriptions = this.isPrivate ? true : false;
        break;
    }
  }

  toggleFilter(category) {
    const filterIndex = this.selectedCategoryIndex(category);
    filterIndex > -1 ? this.selectedFilters.splice(filterIndex, 1) : this.selectedFilters.push(category);
    this.filteredJobs = this.selectedFilters.length > 0 ? this.filterJobs() : this.jobs;
    this.writeFiltersToUrl();
  }

  filterJobs() {
    const filteredJobs = this.jobs.filter(job => job.categories.length > 0)
      .filter(job => {
        return job.categories.every((category) => this._categoryEqualsAll(category) || this._checkFilterInCategoryGroup(category));
      });
    return filteredJobs;
  }

  _checkFilterInCategoryGroup(category) {
    const categoryGroupValues = this.selectedFilters
      .filter(item => item.category === category.category)
      .map(filteredItem => filteredItem.value);
    return categoryGroupValues.length > 0 ? categoryGroupValues.indexOf(category.value) > -1 : true;
  }

  _categoryEqualsAll(category) {
    return category.value.toUpperCase() === 'ALL';
  }

  selectedCategoryIndex(category) {
    return this.selectedFilters.findIndex(item => item.value === category.value && item.category === category.category);
  }

  /**
   * Lee los tokens de filtro de la URL una sola vez, con snapshot y sin
   * suscribirse a queryParamMap, para no abrir la puerta a un bucle de
   * re-aplicacion cuando writeFiltersToUrl navegue (R6.6, D3).
   *
   * Por cada grupo acumula los valores de todos sus nombres aceptados (canonico
   * y alias *_filter suman), recorta espacios y descarta los tokens vacios. Los
   * tokens quedan en pendingUrlFilters a la espera de que lleguen las categorias
   * del cliente para resolverse en applyFiltersFromUrl.
   */
  readFiltersFromUrl() {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    Object.keys(this.pendingUrlFilters).forEach(categoryGroup => {
      this.pendingUrlFilters[ categoryGroup ] = this.urlFilterParams[ categoryGroup ]
        .reduce((tokens, paramName) => tokens.concat(queryParams.getAll(paramName)), [])
        .map(token => token.trim())
        .filter(token => token.length > 0);
    });
  }

  /**
   * Refleja en la URL los filtros activos, para que cualquier busqueda del hub
   * se pueda compartir o guardar en favoritos (R6.1).
   *
   * Cada grupo con filtros se escribe con su nombre canonico y un array de slugs,
   * que Angular serializa repitiendo el parametro. Los grupos vacios y todos los
   * alias *_filter se ponen a null para que Angular los elimine y no queden
   * parametros vacios ni dos nombres contradictorios para el mismo grupo
   * (R6.2, R5.4).
   *
   * `queryParamsHandling: 'merge'` conserva los parametros ajenos a los filtros,
   * como los de campaña (R6.3), y `replaceUrl` evita acumular una entrada de
   * historial por cada clic en un checkbox (R6.4).
   *
   * No hay riesgo de bucle: la URL se lee una unica vez en ngOnInit con snapshot,
   * nadie esta suscrito a queryParamMap y navegar a la misma ruta cambiando solo
   * el query no recrea el componente (R6.6, D3).
   */
  writeFiltersToUrl() {
    const queryParams = {};

    Object.keys(this.urlFilterParams).forEach(categoryGroup => {
      const paramNames = this.urlFilterParams[ categoryGroup ];
      const values = this.selectedFilters
        .filter(filter => filter.category === categoryGroup)
        .map(filter => normalizeSlug(filter.value));

      paramNames.forEach((paramName, index) => {
        const isCanonical = index === 0;
        queryParams[ paramName ] = isCanonical && values.length > 0 ? values : null;
      });
    });

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * Aplica los filtros que venian en la URL. Se llama desde el subscribe de
   * _serializeFilterCategories porque es el primer momento en que existen
   * categorias contra las que resolver los tokens; en ngOnInit todavia no han
   * llegado (R1.6, D3). Para entonces `jobs` ya esta poblado, porque la peticion
   * de categorias se lanza dentro del subscribe de la de ofertas.
   *
   * Empuja a selectedFilters los mismos objetos categoria que empujaria un clic
   * en la UI, de modo que el filtrado y el estado visual (checkboxes y chips)
   * salen gratis sin tocar filterJobs ni el template (R1.2, R1.4).
   *
   * Si ningun token resuelve no toca nada y la lista queda sin filtrar (R4.2).
   */
  applyFiltersFromUrl() {
    const resolvedCategories = Object.keys(this.pendingUrlFilters)
      .reduce((categories, categoryGroup) => categories.concat(
        this._resolveUrlTokens(this.pendingUrlFilters[ categoryGroup ], categoryGroup)
      ), []);

    if (resolvedCategories.length === 0) { return; }

    // La comprobacion de duplicados va dentro del bucle, no en un filter previo,
    // para que cada push sea visible en la siguiente vuelta. Si no, una URL con
    // el mismo valor repetido (?location=madrid&location=Madrid, o el parametro
    // canonico y su alias a la vez) lo colaria dos veces (R2.4).
    resolvedCategories.forEach(category => {
      if (this.selectedCategoryIndex(category) === -1) {
        this.selectedFilters.push(category);
      }
    });

    this.filteredJobs = this.selectedFilters.length > 0 ? this.filterJobs() : this.jobs;
  }

  /**
   * Resuelve los tokens de un grupo a categorias, descartando los que no
   * correspondan a ninguna categoria del cliente (R4.1).
   *
   * Cada token se intenta primero completo. Si no resuelve y contiene comas, se
   * reintenta interpretandolo como lista separada por comas. Ese orden es
   * deliberado: la forma canonica de pasar varios valores es repetir el
   * parametro, y la lista por comas solo se soporta para tolerar enlaces
   * construidos con la convencion antigua. Probando primero el token completo,
   * una categoria que lleve coma en el nombre ('Madrid, Centro') gana sobre la
   * interpretacion de lista (R2.2, R2.3).
   */
  _resolveUrlTokens(tokens: string[], categoryGroup: string) {
    return tokens.reduce((categories, token) => {
      const category = this._findCategoryByToken(token, categoryGroup);
      if (category) { return categories.concat([ category ]); }
      return categories.concat(this._resolveCommaSeparatedToken(token, categoryGroup));
    }, []);
  }

  /**
   * Segundo intento para un token que no ha resuelto completo: partirlo por
   * comas y resolver cada parte. Los tokens que siguen sin resolver se registran
   * en consola, sin mostrar error al usuario, para poder diagnosticar enlaces
   * mal construidos (R4.3, R4.4).
   */
  _resolveCommaSeparatedToken(token: string, categoryGroup: string) {
    const parts = token.split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0);

    return parts.reduce((categories, part) => {
      const category = this._findCategoryByToken(part, categoryGroup);
      if (category) { return categories.concat([ category ]); }
      console.warn(`[client-hub] filtro de URL ignorado, no existe la categoria "${part}" en el grupo ${categoryGroup}`);
      return categories;
    }, []);
  }

  /**
   * Resuelve un token de la URL contra las categorias disponibles de su grupo.
   * Devuelve la categoria encontrada o undefined si el token no corresponde a
   * ninguna, caso en el que quien llama debe descartarlo (R4.1).
   *
   * Acepta dos formas (R3.2):
   *   - el id numerico de la categoria, ?function=482
   *   - el slug del valor, ?function=atencion-al-cliente
   *
   * El slug hace la comparacion insensible a mayusculas, acentos y a la
   * diferencia entre espacios y guiones (R3.3). Se compara siempre contra
   * `value`, nunca contra `display_value`, porque `value` es lo que compara
   * despues _checkFilterInCategoryGroup al filtrar las ofertas.
   */
  _findCategoryByToken(token: string, categoryGroup: string) {
    const normalizedToken = normalizeSlug(token);
    const isNumericToken = /^\d+$/.test(token);

    return this.categoriesFilters[ categoryGroup ]
      .find(category => isNumericToken && String(category.id) === token
        || normalizeSlug(category.value) === normalizedToken);
  }

  filterCategories(search, categoryGroup) {
    this.filteredCategoriesFilters[ categoryGroup ] = this.categoriesFilters[ categoryGroup ].filter(category => {
      return category.value.toLowerCase()
        .indexOf(search.toLowerCase()) > -1;
    });
  }

  filterProfessionCategory(categories: JobCategory[]): JobCategory[] {
    return categories.filter(category => category.category !== 'PRO');
  }

  showModalAskEmployee() {

    const dialogRef = this.dialog.open(JobsSubscriptionFirstStepComponent, {
      disableClose: true,
      width: '480px',
      maxHeight: '40vh',
      panelClass: 'jobs-subscription__modal',
      data: {
        filters: this.categoriesFilters,
        color: this.client.extra_config.appearance.color.primary,
        private: this.isPrivate,
        terms: this.client.terms_and_conditions
      }
    });

    this.componentSubscriptions.dialogRefAfterClosed$ = dialogRef.afterClosed().subscribe(result => {
      if (result !== 'close') this.showModal(result);
    });

  }

  showModal(option) {

    const dialogRef = this.dialog.open(JobsSubscriptionComponent, {
      disableClose: true,
      width: '980px',
      maxHeight: '80vh',
      panelClass: 'jobs-subscription__modal',
      data: {
        filters: this.categoriesFilters,
        color: this.client.extra_config.appearance.color.primary,
        private: this.isPrivate,
        terms: this.client.terms_and_conditions,
        option: option,
        client: this.client
      }
    });

    this.componentSubscriptions.dialogRefComponentInstanceActiveFiltersChange$ = dialogRef.componentInstance.activeFiltersChange.subscribe((subscription: Subscription) => {
      this.createSubscription(subscription, dialogRef);
    });

    this.componentSubscriptions.dialogRefAfterClosedShowModal$ = dialogRef.afterClosed().subscribe(result => {
      if (result == 'back') this.showModalAskEmployee();
    });

  }

  createSubscription(subscription: Subscription, dialogRef) {
    this.componentSubscriptions.clientServiceCreateSubscriptionForClient$ = this.clientService.createSubscriptionForClient(subscription, this.client_uuid).subscribe(
      () => {
        this.alertService.setAlert({ type: 'success', message: 'Subscription was successful' });
        dialogRef.close();
      },
      () => this.alertService.setAlert({ type: 'error', message: 'Error when trying to subscribe' })
    );
  }

  addCookieBot() {
    let script = document.createElement('script');
    script.setAttribute('src', Constants.COOKIE_BOT_URL);
    if (this.client.extra_config.analytics && this.client.extra_config.analytics.cookieBotCode) {
      script.setAttribute('data-cookiefirst-key', this.client.extra_config.analytics.cookieBotCode);
    } else {
      script.setAttribute('data-cookiefirst-key', Constants.COOKIE_BOT_UUID);
    }
    script.setAttribute('type', 'text/javascript');
    document.head.appendChild(script);
  }

}

class JobsByProfession {
  description: string;
  name: string;
  jobs: Job[];
}
